import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { Rnd } from 'react-rnd';
import { useSnackbar } from 'notistack';
import { useForm, FormProvider } from 'react-hook-form';
import { useI18n } from 'iomart-hooks';
import * as pdfjsLib from 'pdfjs-dist';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import RHFImageBase64Field from 'src/components/hook-form/RHFImageBase64Field';
import {
  getPrivateFileBlobById,
  getPrivateFilePageInfo,
  signPrivateFile,
  type PrivateFileItem,
  type PrivateFilePageInfo
} from 'src/utils/privateFiles';

if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = import.meta.env.DEV
    ? new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()
    : `${window.location.origin}${(import.meta.env.BASE_URL ?? '/').replace(/\/$/, '')}/pdf.worker.min.js`;
}

type SignFormValues = {
  signature: string;
};

type SignPdfDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entityType: string;
  entityId: number;
  asset: PrivateFileItem;
};

export function SignPdfDrawer({
  open,
  onClose,
  onSuccess,
  entityType,
  entityId,
  asset
}: SignPdfDrawerProps) {
  const { t } = useI18n(false);
  const { enqueueSnackbar } = useSnackbar();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);
  const [pageInfo, setPageInfo] = useState<PrivateFilePageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const methods = useForm<SignFormValues>({
    defaultValues: { signature: '' }
  });
  const signature = methods.watch('signature');

  const page = pageInfo?.pages?.[pageIndex];
  const pageWidth = page?.width ?? 595;
  const pageHeight = page?.height ?? 842;

  const pageAspect = pageWidth / pageHeight;
  const previewMaxWidth = 1140;
  const previewMaxHeight = 1000;
  const scale = Math.min(previewMaxWidth / pageWidth, previewMaxHeight / pageHeight);
  const previewWidth = Math.round(pageWidth * scale);
  const previewHeight = Math.round(pageHeight * scale);

  const [rndPos, setRndPos] = useState({ x: 0, y: 0, width: 120, height: 40 });
  const [imageAspect, setImageAspect] = useState<number | null>(null);
  const [rotation, setRotation] = useState(0);
  const previewRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const initRndPos = useCallback(() => {
    const pw = previewWidth;
    const ph = previewHeight;
    const ratio = imageAspect ?? 2.5;
    const w = Math.round(pw * 0.25);
    const h = Math.round(w / ratio);
    setRndPos({
      x: Math.round(pw * 0.1),
      y: Math.round(ph * 0.82 - h),
      width: w,
      height: h
    });
    setRotation(0);
  }, [previewWidth, previewHeight, imageAspect]);

  const loadPdf = useCallback(async () => {
    if (!open || !entityType || !entityId || !asset?.id) return;
    setLoading(true);
    try {
      const blob = await getPrivateFileBlobById(entityType, entityId, asset.id);
      const info = await getPrivateFilePageInfo(entityType, entityId, asset.id);
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      const url = URL.createObjectURL(blob);
      pdfUrlRef.current = url;
      setPdfUrl(url);
      setPageInfo(info);
      setPageIndex(0);
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to load PDF'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [open, entityType, entityId, asset?.id, enqueueSnackbar, t]);

  useEffect(() => {
    if (open) {
      loadPdf();
      methods.reset({ signature: '' });
    }
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, [open]);

  const handleClose = () => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);
    setPageInfo(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!signature || !pageInfo?.pages?.[pageIndex]) {
      enqueueSnackbar(t('Select signature image and position it on the document'), { variant: 'warning' });
      return;
    }
    const rect = previewRef.current?.getBoundingClientRect();
    const cw = rect ? rect.width : previewWidth;
    const ch = rect ? rect.height : previewHeight;
    const pw = pageWidth;
    const ph = pageHeight;

    const pdfX = (rndPos.x / cw) * pw;
    const pdfYFromTop = (rndPos.y / ch) * ph;
    const pdfW = (rndPos.width / cw) * pw;
    const imgRatio = imageAspect ?? rndPos.width / rndPos.height;
    const pdfH = pdfW / imgRatio;

    setSubmitting(true);
    try {
      await signPrivateFile(entityType, entityId, asset.id, {
        imageDataUrl: signature,
        pageIndex,
        x: pdfX,
        y: pdfYFromTop,
        width: pdfW,
        height: pdfH,
        rotation,
      });
      enqueueSnackbar(t('Document signed successfully'), { variant: 'success' });
      setSubmitting(false);
      handleClose();
      onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to sign document'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const prevSignature = useRef('');
  useEffect(() => {
    if (!signature) {
      setImageAspect(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const aspect = img.naturalWidth / img.naturalHeight;
      setImageAspect(aspect);
    };
    img.src = signature;
  }, [signature]);

  useEffect(() => {
    if (signature && pageInfo && (prevSignature.current !== signature || imageAspect !== null)) {
      prevSignature.current = signature;
      initRndPos();
    }
  }, [signature, pageInfo, imageAspect, initRndPos]);

  useEffect(() => {
    if (signature && pageInfo) initRndPos();
  }, [pageIndex]);

  useEffect(() => {
    if (!pdfUrl || !pageInfo?.pages?.[pageIndex] || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;
    const run = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({ url: pdfUrl });
        const pdfDoc = await loadingTask.promise;
        if (cancelled) return;
        const page = await pdfDoc.getPage(pageIndex + 1);
        if (cancelled) return;
        const baseScale = Math.min(previewMaxWidth / pageWidth, previewMaxHeight / pageHeight);
        const pixelRatio = Math.min(window.devicePixelRatio || 2, 3);
        const scale = baseScale * pixelRatio;
        const viewport = page.getViewport({ scale });
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const renderTask = page.render({
          canvasContext: ctx,
          viewport
        });
        await renderTask.promise;
      } catch (err) {
        if (!cancelled) enqueueSnackbar((err as Error)?.message || t('Failed to render PDF page'), { variant: 'error' });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [pdfUrl, pageIndex, pageWidth, pageHeight]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 'min(1200px, 100vw)' }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 } }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', px: 2, pt: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexShrink: 0 }}>
          <Typography variant="h6">{t('Sign document')}: {asset?.fileKey}</Typography>
          <IconButton onClick={handleClose}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>

        <Scrollbar
          mobileBehavior="custom"
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Stack spacing={2} sx={{ py: 2 }}>
          <FormProvider {...methods}>
            <RHFImageBase64Field name="signature" label={t('Signature image')} noT={false} />
          </FormProvider>

          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && pdfUrl && pageInfo && (
            <>
              {pageInfo.pageCount > 1 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('Page')}</InputLabel>
                  <Select
                    value={pageIndex}
                    label={t('Page')}
                    onChange={(e) => setPageIndex(Number(e.target.value))}
                  >
                    {Array.from({ length: pageInfo.pageCount }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {t('Page')} {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('Place signature on the document (portrait and landscape supported)')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <IconButton
                      size="small"
                      onClick={() => setRotation((r) => (r - 15 + 360) % 360)}
                      sx={{ p: 0.5 }}
                      title={t('Rotate left')}
                    >
                      <Iconify icon="eva:minus-fill" width={18} />
                    </IconButton>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32 }}>
                      {rotation}°
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setRotation((r) => (r + 15) % 360)}
                      sx={{ p: 0.5 }}
                      title={t('Rotate right')}
                    >
                      <Iconify icon="eva:plus-fill" width={18} />
                    </IconButton>
                  </Stack>
                </Stack>
                <Box
                  ref={previewRef}
                  sx={{
                    position: 'relative',
                    width: previewWidth,
                    minWidth: 0,
                    flexShrink: 0,
                    height: previewHeight,
                    overflow: 'hidden',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    bgcolor: 'grey.100'
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    style={{
                      width: previewWidth,
                      height: previewHeight,
                      maxWidth: '100%',
                      display: 'block'
                    }}
                  />
                  {signature && imageAspect !== null && (
                    <Rnd
                      size={{ width: rndPos.width, height: rndPos.height }}
                      position={{ x: rndPos.x, y: rndPos.y }}
                      onDragStop={(_, d) => setRndPos((p) => ({ ...p, x: d.x, y: d.y }))}
                      onResizeStop={(_, __, ref, ___, pos) => {
                        const w = ref.offsetWidth;
                        const h = Math.round(w / imageAspect);
                        setRndPos((p) => ({ ...p, width: w, height: h, x: pos.x, y: pos.y }));
                      }}
                      lockAspectRatio={imageAspect}
                      minWidth={40}
                      minHeight={20}
                      bounds="parent"
                      style={{
                        zIndex: 10,
                        border: '2px solid #1976d2',
                        borderRadius: 1,
                        overflow: 'hidden',
                        background: 'transparent',
                        boxShadow: '0 0 0 1px rgba(255,255,255,0.95), 0 0 0 3px #1976d2, 0 0 0 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      <Box
                      component="img"
                      src={signature}
                      alt="signature"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        transform: `rotate(${rotation}deg)`,
                      }}
                    />
                    </Rnd>
                  )}
                </Box>
              </Box>
            </>
          )}
          </Stack>
        </Scrollbar>
      </Box>

      {!loading && pdfUrl && pageInfo && (
        <Box sx={{ width: '100%', px: 2, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleClose}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!signature || imageAspect === null || submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <Iconify icon="eva:edit-2-fill" width={16} />}
            >
              {submitting ? t('Signing...') : t('Sign')}
            </Button>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}
